import {
  Bot,
  Brain,
  Database,
  Eye,
  EyeOff,
  Info,
  Key,
  Save,
  ShieldCheck,
  SlidersHorizontal,
} from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import Select from "react-select";


const AIAgentSettings = () => {
  const [selectedModel, setSelectedModel] = useState(null);
  const [systemPrompt, setSystemPrompt] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [showApiKey, setShowApiKey] = useState(false);
  const [tokenLimit, setTokenLimit] = useState(50);
  const [saved, setSaved] = useState(false);
  const textareaRef = useRef(null);

  const modelOptions = [
    { value: "gpt-4o", label: "GPT-4o — Best for complex reasoning" },
    { value: "gpt-4o-mini", label: "GPT-4o mini — Faster & cost efficient" },
    { value: "gpt-3.5", label: "GPT-3.5 — Basic tasks" },
  ];

  const tokenPercent = ((tokenLimit - 1) / 499) * 100;


  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  return (
    <main className="app-wrapper url-list-page" id="aisPage">
      <div className="container-fluid">
        <section className="py-2 mb-3 d-flex align-items-center justify-content-end">
          <button
            className={`btn btn-primary ais-save-btn${saved ? " ais-saved" : ""}`}
            onClick={handleSave}
          >
            <Save size={15} className="me-2" />
            {saved ? "Saved!" : "Save Changes"}
          </button>
        </section>

        <div className="row g-3 ais-row">
          {/* ── LEFT COLUMN ── */}
          <div className="col-12 col-xl-7 ais-col">

            {/* AI Model + System Prompt */}
            <div className="ais-card">
              <div className="ais-card-header">
                <div className="ais-card-icon">
                  <Brain size={18} />
                </div>
                <div>
                  <h3 className="ais-card-title">AI Model</h3>
                  <p className="ais-card-desc">
                    Select the language model that powers your agent
                  </p>
                </div>
              </div>
              <div className="ais-card-body">
                <label className="form-label">Model</label>
                <Select
                  className="kb-search-select"
                  classNamePrefix="kb-select"
                  placeholder="Search or select a model..."
                  isSearchable
                  value={selectedModel}
                  options={modelOptions}
                  onChange={(selected) => setSelectedModel(selected)}
                />
              </div>

              <hr className="ais-divider" />

              <div className="ais-card-header">
                <div className="ais-card-icon">
                  <Bot size={18} />
                </div>
                <div>
                  <h3 className="ais-card-title">System Prompt</h3>
                  <p className="ais-card-desc">
                    Define the behavior, tone, and persona of your AI agent
                  </p>
                </div>
              </div>
              <div className="ais-card-body">
                <div className="ais-prompt-label-row">
                  <label className="form-label mb-0">Prompt Content</label>
                </div>
                <textarea
                  ref={textareaRef}
                  className="form-control ais-textarea"
                  value={systemPrompt}
                  rows={8}
                  placeholder="e.g. You are a helpful customer support assistant for Acme Corp. Always respond professionally and concisely..."
                  onChange={(e) => setSystemPrompt(e.target.value)}
                />
                <p className="ais-char-row">{systemPrompt.length} characters</p>
              </div>
              <div className="ais-card-footer-info">
                <Info size={14} className="flex-shrink-0" />
                <span>
                  Provide clear instructions to help your AI agent deliver
                  accurate and consistent responses.
                </span>
              </div>
            </div>
          </div>

          {/* ── RIGHT COLUMN ── */}
          <div className="col-12 col-xl-5 ais-col">

            {/* Monthly Token Limit + API Key */}
            <div className="ais-card">
              <div className="ais-card-header">
                <div className="ais-card-icon ais-card-icon--green">
                  <SlidersHorizontal size={18} />
                </div>
                <div>
                  <h3 className="ais-card-title">Monthly Token Limit</h3>
                  <p className="ais-card-desc">Set a usage cap to control costs</p>
                </div>
              </div>
              <div className="ais-card-body">
                <div className="ais-token-display">
                  <Database size={26} className="ais-token-db-icon" />
                  <span className="ais-token-number">{tokenLimit}M</span>
                  <span className="ais-token-unit">tokens / month</span>
                </div>

                <div className="ais-range-wrap">
                  <input
                    type="range"
                    min="1"
                    max="500"
                    value={tokenLimit}
                    className="ais-range"
                    style={{ "--fill": `${tokenPercent}%` }}
                    onChange={(e) => setTokenLimit(Number(e.target.value))}
                  />
                  <div className="ais-range-labels">
                    <span>1M</span>
                    <span>250M</span>
                    <span>500M</span>
                  </div>
                </div>

                <div className="ais-custom-val-row">
                  <p className="ais-custom-val-label">Custom value (M)</p>
                  <input
                    type="number"
                    min="1"
                    max="500"
                    value={tokenLimit}
                    className="form-control ais-token-input"
                    onChange={(e) => {
                      let v = Number(e.target.value || 1);
                      if (v < 1) v = 1;
                      if (v > 500) v = 500;
                      setTokenLimit(v);
                    }}
                  />
                </div>

                <div className="ais-token-info">
                  <Info size={14} className="flex-shrink-0" />
                  <span>
                    You will be notified when usage reaches 80% of the limit.
                  </span>
                </div>
              </div>

              <hr className="ais-divider" />

              <div className="ais-card-header">
                <div className="ais-card-icon ais-card-icon--amber">
                  <Key size={18} />
                </div>
                <div>
                  <h3 className="ais-card-title">API Key</h3>
                  <p className="ais-card-desc">
                    Used for authenticated model requests
                  </p>
                </div>
              </div>
              <div className="ais-card-body">
                <div className="ais-api-label-row">
                  <label className="form-label mb-0">Secret Key</label>
                  <button
                    type="button"
                    className="ais-eye-btn"
                    onClick={() => setShowApiKey(!showApiKey)}
                  >
                    {showApiKey ? (
                      <><EyeOff size={13} /> Hide</>
                    ) : (
                      <><Eye size={13} /> Reveal</>
                    )}
                  </button>
                </div>
                <textarea
                  className={`form-control ais-api-textarea${showApiKey ? " show-key" : " hide-key"}`}
                  rows={4}
                  value={apiKey}
                  placeholder="Paste your API key here..."
                  onChange={(e) => setApiKey(e.target.value)}
                />
                <div className="ais-security-badge">
                  <ShieldCheck size={13} />
                  Encrypted at rest
                </div>
                <p className="ais-api-hint">
                  Your key is never exposed in responses or logs.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default AIAgentSettings;
